import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Upload, Search, ChevronDown, ChevronUp, ChevronRight, RefreshCw, Package,
  AlertTriangle, CheckCircle, Calendar, X, Lightbulb,
  Info, Star, SlidersHorizontal, ArrowUpDown, Users, Inbox, ShoppingCart,
  FileText, History,
} from 'lucide-react';
import { StockRequestDetailModal, StockHistoryModal } from './StockRequestModal';

const BRANCH_ID = 'C30';
const enconOrange = '#ee7203';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, dp = 0) => {
  if (n == null || n === '' || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};
const fmtCcy = (n) => {
  if (n == null || n === '' || isNaN(n)) return '—';
  return `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const coverClass = (days) => {
  if (days == null) return '';
  if (days === 0) return 'text-red-700 font-bold';
  if (days < 20) return 'text-red-600 font-semibold';
  if (days < 50) return 'text-amber-600 font-semibold';
  return 'text-green-600';
};
const shortDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
  catch { return null; }
};

// ─── Suggestion Card ──────────────────────────────────────────────────────────
function SuggestionCard({ supplier, onClick, active, isBase }) {
  return (
    <button
      onClick={onClick}
      data-testid={`suggestion-card-${supplier.supplier_code}`}
      className={`text-left px-3 py-3 rounded-xl border-2 transition-all hover:shadow-md flex-shrink-0 min-w-[160px] max-w-[200px] relative ${
        active
          ? 'border-purple-400 bg-purple-50 shadow-md'
          : isBase
          ? 'border-orange-300 bg-orange-50/40 hover:border-orange-500'
          : 'border-gray-200 bg-white hover:border-purple-300'
      }`}
    >
      {isBase && (
        <span className="absolute top-1.5 right-1.5">
          <Star className="w-3 h-3 fill-orange-400 text-orange-400" />
        </span>
      )}
      <div className="font-semibold text-xs text-gray-800 truncate pr-4">{supplier.supplier_name}</div>
      <div className="text-[11px] text-gray-500 mt-0.5 truncate">
        {supplier.prefixes?.slice(0, 5).join(', ')}{supplier.prefixes?.length > 5 ? ` +${supplier.prefixes.length - 5}` : ''}
      </div>
      <div className="mt-2 flex items-center justify-between gap-1">
        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: '#fff7ed', color: enconOrange }}>
          {supplier.item_count} line{supplier.item_count !== 1 ? 's' : ''}
        </span>
        {supplier.total_est_value > 0 && (
          <span className="text-[11px] text-gray-500">{fmtCcy(supplier.total_est_value)}</span>
        )}
      </div>
    </button>
  );
}

// ─── Base Suppliers Modal ─────────────────────────────────────────────────────
function BaseSuppliersModal({ isOpen, onClose, suppliers, baseSupplierCodes, onSave }) {
  const [tab, setTab] = useState('selected');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set(baseSupplierCodes));
  const saveTimerRef = useRef(null);

  // Sync with parent when opened
  useEffect(() => {
    if (isOpen) setSelected(new Set(baseSupplierCodes));
  }, [isOpen, baseSupplierCodes]);

  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code); else next.add(code);
    setSelected(next);
    // Auto-save debounced
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave([...next]);
    }, 600);
  };

  const searchLower = search.toLowerCase().trim();
  const allSuppliers = [...suppliers].sort((a, b) =>
    a.supplier_name.localeCompare(b.supplier_name)
  );

  const visibleSuppliers = allSuppliers.filter(s => {
    const matchSearch = !searchLower ||
      s.supplier_name.toLowerCase().includes(searchLower) ||
      s.supplier_code.toLowerCase().includes(searchLower);
    if (!matchSearch) return false;
    if (tab === 'selected') return selected.has(s.supplier_code);
    return !selected.has(s.supplier_code); // 'others'
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={(e) => e.stopPropagation()}>
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ width: '480px', maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 fill-orange-400 text-orange-400" />
            <span className="font-bold text-gray-800 text-lg">Base Suppliers</span>
            {selected.size > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white"
                style={{ backgroundColor: enconOrange }}>
                {selected.size} selected
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search suppliers…"
              className="pl-9 pr-9"
              autoFocus
              data-testid="base-supplier-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mx-5 mt-2 border-b">
          {[
            { id: 'selected', label: `Base Suppliers${selected.size > 0 ? ` (${selected.size})` : ''}` },
            { id: 'others', label: `All Others (${suppliers.length - selected.size})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-orange-500 text-gray-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={tab === t.id ? { borderColor: enconOrange } : {}}
              data-testid={`base-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
          {visibleSuppliers.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              {tab === 'selected' && selected.size === 0
                ? 'No base suppliers selected yet. Search and tick suppliers in the "All Others" tab.'
                : 'No suppliers match your search.'}
            </div>
          ) : (
            visibleSuppliers.map(s => (
              <label
                key={s.supplier_code}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                data-testid={`base-supplier-row-${s.supplier_code}`}
              >
                <Checkbox
                  checked={selected.has(s.supplier_code)}
                  onCheckedChange={() => toggle(s.supplier_code)}
                  data-testid={`base-supplier-cb-${s.supplier_code}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-800 truncate">{s.supplier_name}</div>
                  <div className="text-xs text-gray-400">{s.supplier_code} · {s.count} products</div>
                </div>
                {selected.has(s.supplier_code) && (
                  <Star className="w-4 h-4 flex-shrink-0 fill-orange-400 text-orange-400" />
                )}
              </label>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-400">Changes save automatically</p>
          <Button size="sm" onClick={onClose} style={{ backgroundColor: enconOrange }} className="text-white">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Header ──────────────────────────────────────────────────────────
function SortableHeader({ label, column, sortConfig, onSort, className = '', align = 'right', theme = 'default' }) {
  const isActive = sortConfig.column === column;
  const ActiveIcon = sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;

  const themeClasses = {
    default: 'text-gray-500 hover:bg-gray-100',
    orange:  'text-orange-700 hover:bg-orange-100',
    purple:  'text-purple-700 hover:bg-purple-100',
  };
  const tc = themeClasses[theme] || themeClasses.default;

  return (
    <th
      className={`px-2 py-3 cursor-pointer select-none group transition-colors ${tc} text-xs uppercase tracking-wide ${className}`}
      onClick={() => onSort(column)}
      data-testid={`sort-header-${column}`}
    >
      <span className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
        {isActive ? (
          <ActiveIcon className="w-3 h-3 flex-shrink-0 text-orange-500" />
        ) : (
          <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400 hidden group-hover:block" />
        )}
        <span className={isActive ? 'text-orange-600 font-semibold' : 'font-semibold'}>{label}</span>
      </span>
    </th>
  );
}

// ─── Filter pill ──────────────────────────────────────────────────────────────
function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all whitespace-nowrap ${
        active
          ? 'text-white border-orange-500 shadow-sm'
          : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-gray-800'
      }`}
      style={active ? { backgroundColor: enconOrange, borderColor: enconOrange } : {}}
    >
      {children}
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function StockOrderingView({ user, onDailyInfoChange, onRefreshReady, onUploadDailyReady, onUploadingChange, deepLinkRequestUser }) {
  const dailyFileRef = useRef(null);

  // Data
  const [suppliers, setSuppliers] = useState([]);
  const [suggestions, setSuggestions] = useState({ suppliers: [], total_suggestions: 0, supplier_count: 0 });
  const [items, setItems] = useState([]);
  const [dailyInfo, setDailyInfo] = useState({ has_daily: false, has_prev_daily: false, latest_upload_date: null, prev_upload_date: null });
  const [masterInfo, setMasterInfo] = useState(null);
  const [baseSupplierCodes, setBaseSupplierCodes] = useState([]);
  const [stockRequests, setStockRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequestUser, setSelectedRequestUser] = useState(null);
  const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);

  // UI state
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [uploadingDaily, setUploadingDaily] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [baseSuppliersModalOpen, setBaseSuppliersModalOpen] = useState(false);
  const [extraColumnsOpen, setExtraColumnsOpen] = useState(false);

  // Table filters (supplier/prefix/search still sent to API)
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedPrefixes, setSelectedPrefixes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSuggestionSupplier, setActiveSuggestionSupplier] = useState(null);

  // New: additional client-side filters + sort
  const [activeFilters, setActiveFilters] = useState([]); // 'relevant' | 'under-50' | 'over-50'
  const [sortConfig, setSortConfig] = useState({ column: '_to_order', direction: 'desc' });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  // Panel open/close
  const [stockRequestsOpen, setStockRequestsOpen] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [notesModalItem, setNotesModalItem] = useState(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const h = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) setFilterDropdownOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ─── Derived ────────────────────────────────────────────────────────────────
  const availablePrefixes = useMemo(() => {
    if (!selectedSupplier) return [];
    return suppliers.find(s => s.supplier_code === selectedSupplier)?.prefixes || [];
  }, [selectedSupplier, suppliers]);

  // Stock request product codes for pinning (unpurchased lines only)
  // Map: productCode → { user_email, user_name, created_at } — grouped by user so
  // all requests from the same person share a single pin-header in the main table.
  const stockRequestPinMap = useMemo(() => {
    const map = new Map();
    // Sort requests by created_at descending so the most recent request wins for a product
    const sorted = [...stockRequests].sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    for (const req of sorted) {
      for (const line of (req.lines || []).filter(l => !l.purchased)) {
        if (!map.has(line.product_code)) {
          map.set(line.product_code, {
            user_email: req.user_email,
            user_name: req.user_name || req.user_email || 'Unknown',
            created_at: req.created_at,
          });
        }
      }
    }
    return map;
  }, [stockRequests]);

  // Group requests by user — even across different days.
  // Each group shows total unpurchased lines + request count badge.
  const groupedRequests = useMemo(() => {
    const byUser = new Map();
    for (const req of stockRequests) {
      const unpurchasedLines = req.lines ? req.lines.filter(l => !l.purchased) : [];
      const unpurchasedCount = unpurchasedLines.length || (req.product_count || 0);
      if (unpurchasedCount === 0) continue; // skip fully-purchased requests
      const key = req.user_email || req.user_name || 'unknown';
      if (!byUser.has(key)) {
        byUser.set(key, {
          userEmail: req.user_email,
          userName: req.user_name || req.user_email || 'Unknown',
          requests: [],
          totalUnpurchased: 0,
          totalLines: 0,
          latestDate: req.created_at,
          suppliers: new Set(),
        });
      }
      const group = byUser.get(key);
      group.requests.push(req);
      group.totalUnpurchased += unpurchasedCount;
      group.totalLines += req.lines?.length || 0;
      // Keep the most recent date
      if (req.created_at && (!group.latestDate || req.created_at > group.latestDate)) {
        group.latestDate = req.created_at;
      }
      for (const s of (req.suppliers || [])) group.suppliers.add(s);
    }
    return [...byUser.values()];
  }, [stockRequests]);

  // Client-side filter + sort
  const displayItems = useMemo(() => {
    let filtered = [...items];
    if (activeFilters.includes('relevant')) {
      filtered = filtered.filter(i => (i.live_stock_qty > 0) || (i.live_last_3m > 0));
    }
    if (activeFilters.includes('under-50')) {
      filtered = filtered.filter(i => i.live_cover_days != null && i.live_cover_days < 50);
    }
    if (activeFilters.includes('over-50')) {
      filtered = filtered.filter(i => i.live_cover_days != null && i.live_cover_days >= 50);
    }
    // Daily Comparison: show only products where on_order_quantity INCREASED vs yesterday
    // — meaning goods were ordered the previous day
    if (activeFilters.includes('daily-comparison')) {
      filtered = filtered.filter(i => {
        const cur = i.on_order_quantity ?? 0;
        const prev = i.prev_on_order_quantity ?? 0;
        return cur > prev;
      });
    }
    // Pre-compute to_order using correct formulas:
    // 50 day target = 3M qty / 90 * 50
    // To Order = 50 day target - current stock - allocated - on order
    filtered = filtered.map(i => {
      const last3m = i.live_last_3m ?? i.last_3m_qty ?? 0;
      const onOrder = i.on_order_quantity ?? 0;
      const allocated = i.allocated_stock ?? 0;
      const stock = i.live_stock_qty ?? 0;
      const packMoq = i.pack_moq || 1;

      const target50d = (last3m / 90) * 50;
      const rawToOrder = target50d - stock - allocated - onOrder;
      const toOrder = rawToOrder <= 0 ? 0 : Math.ceil(rawToOrder / packMoq) * packMoq;

      return { ...i, _to_order: toOrder, _target50d: Math.round(target50d) };
    });
    if (sortConfig.column) {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      filtered.sort((a, b) => {
        const av = a[sortConfig.column];
        const bv = b[sortConfig.column];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string') return av.localeCompare(bv) * dir;
        return (av - bv) * dir;
      });
    }
    // ── Pin requested items to top (unless a coverage or daily-comparison filter is active) ──
    const has50Filter = activeFilters.includes('under-50') || activeFilters.includes('over-50') || activeFilters.includes('daily-comparison');
    if (!has50Filter && stockRequestPinMap.size > 0) {
      const pinned = filtered.filter(i => stockRequestPinMap.has(i.product_code));
      const rest = filtered.filter(i => !stockRequestPinMap.has(i.product_code));
      filtered = [...pinned, ...rest];
    }
    return filtered;
  }, [items, activeFilters, sortConfig, stockRequestPinMap]);

  const toggleFilter = (f) => setActiveFilters(prev =>
    prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]
  );

  // ─── Shared render-row list ──────────────────────────────────────────────────
  const BANNER_ROW_H = 36;

  const renderRows = useMemo(() => {
    const hasCoverageFilter = activeFilters.includes('under-50') || activeFilters.includes('over-50') || activeFilters.includes('daily-comparison');
    const rows = [];
    let lastUserEmail = null;

    displayItems.forEach((item) => {
      const pinInfo = (!hasCoverageFilter && stockRequestPinMap.size > 0)
        ? stockRequestPinMap.get(item.product_code)
        : null;

      if (pinInfo && pinInfo.user_email !== lastUserEmail) {
        lastUserEmail = pinInfo.user_email;
        const userReqCount = groupedRequests.find(g => g.userEmail === pinInfo.user_email)?.requests?.length || 1;
        const diff = pinInfo.created_at ? Date.now() - new Date(pinInfo.created_at) : 0;
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(mins / 60);
        const days = Math.floor(hrs / 24);
        const requestedAgo = !pinInfo.created_at ? '' : days > 0 ? `${days}d ago` : hrs > 0 ? `${hrs}h ago` : mins > 0 ? `${mins}m ago` : 'just now';
        rows.push({ type: 'banner', key: `pin-header-${pinInfo.user_email}`, pinInfo, userReqCount, requestedAgo });
      }

      if (!pinInfo) lastUserEmail = null;

      rows.push({ type: 'item', key: item.product_code, item, isPinned: !!pinInfo });
    });

    return rows;
  }, [displayItems, activeFilters, stockRequestPinMap, groupedRequests]);


  const loadSuppliers = useCallback(async () => {
    try {
      const res = await api.get(`${API}/stock-ordering/suppliers`, { params: { branch_id: BRANCH_ID } });
      setSuppliers(res.data.suppliers || []);
    } catch { /* silent */ }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const res = await api.get(`${API}/stock-ordering/suggestions`, { params: { branch_id: BRANCH_ID } });
      setSuggestions(res.data);
    } catch { /* silent */ }
    finally { setLoadingSuggestions(false); }
  }, []);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const params = { branch_id: BRANCH_ID };
      if (selectedSupplier) params.supplier_code = selectedSupplier;
      if (selectedPrefixes.length) params.prefixes = selectedPrefixes.join(',');
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await api.get(`${API}/stock-ordering/items`, { params });
      setItems(res.data.items || []);
      const di = {
        has_daily: res.data.has_daily,
        has_prev_daily: res.data.has_prev_daily,
        latest_upload_date: res.data.latest_upload_date,
        prev_upload_date: res.data.prev_upload_date,
      };
      setDailyInfo(di);
      // Notify parent header
      if (onDailyInfoChange) {
        onDailyInfoChange({
          has_daily: di.has_daily,
          latestLabel: di.latest_upload_date ? shortDate(di.latest_upload_date) : null,
          prevLabel: di.prev_upload_date ? shortDate(di.prev_upload_date) : null,
          raw_latest: di.latest_upload_date || null,
        });
      }
    } catch { /* silent */ }
    finally { setLoadingItems(false); }
  }, [selectedSupplier, selectedPrefixes, searchQuery, onDailyInfoChange]);

  const loadMasterInfo = useCallback(async () => {
    try {
      const res = await api.get(`${API}/stock-ordering/master/info`, { params: { branch_id: BRANCH_ID } });
      setMasterInfo(res.data);
    } catch { setMasterInfo({ has_master: false }); }
  }, []);

  const loadBaseSuppliers = useCallback(async () => {
    try {
      const res = await api.get(`${API}/stock-ordering/base-suppliers`, { params: { branch_id: BRANCH_ID } });
      setBaseSupplierCodes(res.data.supplier_codes || []);
    } catch { /* silent */ }
  }, []);

  const loadStockRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const res = await api.get(`${API}/stock-ordering/requests`, { params: { branch_id: BRANCH_ID } });
      setStockRequests(res.data.requests || []);
    } catch { /* silent */ }
    finally { setLoadingRequests(false); }
  }, []);

  useEffect(() => {
    loadMasterInfo();
    loadSuppliers();
    loadSuggestions();
    loadBaseSuppliers();
    loadStockRequests();
  }, []);

  // Deep-link: auto-open request modal for a specific user email
  useEffect(() => {
    if (!deepLinkRequestUser) return;
    // Wait until requests have loaded
    if (groupedRequests.length === 0) return;
    const group = groupedRequests.find(
      g => (g.userEmail || '').toLowerCase() === deepLinkRequestUser.toLowerCase()
    );
    if (group) {
      setSelectedRequestUser(group.userEmail);
      setShowRequestDetailModal(true);
      // Scroll the stock requests panel into view
      const el = document.getElementById('stock-requests-panel');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [deepLinkRequestUser, groupedRequests]);

  // Register toolbar bridge callbacks with parent (StockManagement header)
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(() => { loadSuggestions(); loadItems(); });
    if (onUploadDailyReady) onUploadDailyReady(() => dailyFileRef.current?.click());
  }, [onRefreshReady, onUploadDailyReady]);

  // Notify parent of upload state
  useEffect(() => {
    if (onUploadingChange) onUploadingChange(uploadingDaily);
  }, [uploadingDaily, onUploadingChange]);

  useEffect(() => { loadItems(); }, [selectedSupplier, selectedPrefixes, searchQuery]);

  // ─── Save base suppliers ──────────────────────────────────────────────────
  const handleSaveBaseSuppliers = async (codes) => {
    setBaseSupplierCodes(codes);
    try {
      await api.put(`${API}/stock-ordering/base-suppliers`, { supplier_codes: codes }, {
        params: { branch_id: BRANCH_ID }
      });
      // Reload suggestions so ordering updates
      loadSuggestions();
    } catch {
      toast.error('Failed to save base suppliers');
    }
  };

  // ─── Upload daily ─────────────────────────────────────────────────────────
  const handleDailyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) { toast.error('Please upload a .xlsx file'); return; }
    setUploadingDaily(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branch_id', BRANCH_ID);
    try {
      const res = await api.post(`${API}/stock-ordering/daily/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Daily upload complete — ${res.data.row_count.toLocaleString()} rows loaded`);
      await Promise.all([loadItems(), loadSuggestions()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDaily(false);
      if (dailyFileRef.current) dailyFileRef.current.value = '';
    }
  };

  // ─── Toggle ordered ───────────────────────────────────────────────────────
  const toggleOrdered = async (item) => {
    const newVal = !item.is_ordered;
    setItems(prev => prev.map(i => i.product_code === item.product_code ? { ...i, is_ordered: newVal } : i));
    try {
      await api.put(`${API}/stock-ordering/items/${item.product_code}`, {
        is_ordered: newVal,
        on_order_at_tick: item.on_order_quantity ?? 0,
      }, { params: { branch_id: BRANCH_ID } });

      if (newVal) {
        toast.success(`${item.product_code} marked as ordered`);
        // If this item is pinned from a stock request, also mark that request line as purchased
        if (stockRequestPinMap.has(item.product_code)) {
          for (const req of stockRequests) {
            const lineIdx = (req.lines || []).findIndex(l => l.product_code === item.product_code && !l.purchased);
            if (lineIdx >= 0) {
              try {
                const res = await api.patch(
                  `${API}/stock-ordering/requests/${req.id}/lines`,
                  { lines: [{ idx: lineIdx, purchased: true }] },
                  { params: { branch_id: BRANCH_ID } }
                );
                setStockRequests(prev => prev.map(r =>
                  r.id === req.id ? { ...r, lines: res.data.lines, product_count: (r.product_count || 1) - 1 } : r
                ));
              } catch { /* silent */ }
              break;
            }
          }
        }
      }
    } catch {
      setItems(prev => prev.map(i => i.product_code === item.product_code ? { ...i, is_ordered: !newVal } : i));
      toast.error('Failed to update ordered state');
    }
  };

  const saveNotes = async (item, notes) => {
    // Optimistic local update
    setItems(prev => prev.map(i =>
      i.product_code === item.product_code
        ? { ...i, notes, notes_date: notes.trim() ? new Date().toISOString() : null }
        : i
    ));
    try {
      await api.put(`${API}/stock-ordering/items/${item.product_code}`, { notes }, {
        params: { branch_id: BRANCH_ID }
      });
    } catch { toast.error('Failed to save notes'); }
  };

  const deleteStockRequest = async (requestId) => {
    try {
      await api.delete(`${API}/stock-ordering/requests/${requestId}`, { params: { branch_id: BRANCH_ID } });
      setStockRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Stock request deleted');
      window.dispatchEvent(new Event('c30:stock-changed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete request');
    }
  };

  // ─── Suggestion card click ─────────────────────────────────────────────────
  const handleSuggestionClick = (supplier) => {
    if (activeSuggestionSupplier === supplier.supplier_code) {
      setActiveSuggestionSupplier(null);
      setSelectedSupplier('');
      setSelectedPrefixes([]);
    } else {
      setActiveSuggestionSupplier(supplier.supplier_code);
      setSelectedSupplier(supplier.supplier_code);
      setSelectedPrefixes(supplier.prefixes || []);
    }
  };

  const handleSupplierChange = (code) => {
    setSelectedSupplier(code);
    setSelectedPrefixes([]);
    setActiveSuggestionSupplier(null);
  };

  const togglePrefix = (prefix) => {
    setSelectedPrefixes(prev => prev.includes(prefix) ? prev.filter(p => p !== prefix) : [...prev, prefix]);
  };

  const clearFilters = () => {
    setSelectedSupplier('');
    setSelectedPrefixes([]);
    setSearchQuery('');
    setActiveSuggestionSupplier(null);
    setActiveFilters([]);
    setSortConfig({ column: '_to_order', direction: 'desc' });
  };

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const hasFilters = selectedSupplier || selectedPrefixes.length || searchQuery || activeFilters.length ||
    (sortConfig.column && !(sortConfig.column === '_to_order' && sortConfig.direction === 'desc'));
  const baseSet = new Set(baseSupplierCodes);

  // ─── No master ─────────────────────────────────────────────────────────────
  if (masterInfo && !masterInfo.has_master) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-700">No Master File Uploaded</h3>
        <p className="text-gray-500 text-center max-w-sm">
          Ask your administrator to upload the SOS master file in <strong>Settings &gt; SoS Settings</strong>.
        </p>
      </div>
    );
  }

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Hidden daily upload file input — triggered from parent header */}
      <input ref={dailyFileRef} type="file" accept=".xlsx" className="hidden" onChange={handleDailyUpload} />

      {/* ── Stock Requests Panel ── */}
      <div id="stock-requests-panel" className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ background: '#f5f0ff' }}>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 flex-shrink-0" style={{ color: '#7c3aed' }} />
            <span className="font-semibold" style={{ color: '#5b21b6' }}>Stock Requests</span>
            {groupedRequests.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full text-white font-semibold"
                style={{ backgroundColor: enconOrange }}
                data-testid="stock-requests-badge">
                {groupedRequests.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowHistoryModal(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-all hover:shadow-sm"
              style={{ borderColor: enconOrange, color: enconOrange }}
              title="View history"
              data-testid="stock-requests-history-btn"
            >
              <History className="w-3.5 h-3.5" />
              History
            </button>
            <button
              onClick={loadStockRequests}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
              title="Refresh requests"
              data-testid="stock-requests-refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-400 ${loadingRequests ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setStockRequestsOpen(o => !o)}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
              title={stockRequestsOpen ? 'Collapse' : 'Expand'}
              data-testid="stock-requests-collapse"
            >
              {stockRequestsOpen
                ? <ChevronUp className="w-4 h-4 text-gray-400" />
                : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
          </div>
        </div>

        {stockRequestsOpen && (
          loadingRequests ? (
            <div className="py-4 text-center text-sm text-gray-400">Loading…</div>
          ) : groupedRequests.length === 0 ? (
            <div className="px-5 py-4 flex items-center gap-2 text-sm text-gray-400">
              <Inbox className="w-4 h-4" />
              There are currently no product requests
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-2.5 text-left font-semibold">User</th>
                    <th className="px-4 py-2.5 text-center font-semibold">To Order</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Suppliers</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Latest Request</th>
                    <th className="px-4 py-2.5 text-center font-semibold">Total Lines</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groupedRequests.map(group => {
                    const suppliersArr = [...group.suppliers];
                    return (
                      <tr
                        key={group.userEmail}
                        className="hover:bg-purple-50/40 transition-colors cursor-pointer group"
                        data-testid={`stock-req-row-${group.userEmail}`}
                        onClick={() => {
                          setSelectedRequestUser(group.userEmail);
                          setShowRequestDetailModal(true);
                        }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-800 group-hover:text-purple-700 transition-colors underline-offset-2 group-hover:underline">
                              {group.userName}
                            </span>
                            {group.requests.length > 1 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white"
                                style={{ backgroundColor: '#7c3aed' }}
                                title={`${group.requests.length} separate requests`}
                                data-testid={`stock-req-count-${group.userEmail}`}
                              >
                                {group.requests.length}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold"
                            style={{ backgroundColor: group.totalUnpurchased > 0 ? enconOrange : '#9ca3af' }}>
                            {group.totalUnpurchased}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {suppliersArr.length > 0
                            ? <span className="text-xs">{suppliersArr.slice(0, 3).join(', ')}{suppliersArr.length > 3 ? ` +${suppliersArr.length - 3}` : ''}</span>
                            : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {group.latestDate ? new Date(group.latestDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 text-center">
                          {group.totalLines} line{group.totalLines !== 1 ? 's' : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* ── Suggestions Panel ── */}
      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        {/* Header — non-clickable row with separate toggle */}
        <div className="flex items-center justify-between px-5 py-3.5" style={{ background: '#fff3e0' }}>
          <div className="flex items-center gap-2 flex-1">
            <Lightbulb className="w-5 h-5 flex-shrink-0" style={{ color: '#ea6c00' }} />
            <span className="font-semibold" style={{ color: '#c45100' }}>Order Suggestions</span>
          </div>
          {/* Base Suppliers button */}
          <button
            onClick={() => setBaseSuppliersModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:shadow-sm mr-3"
            style={{ borderColor: enconOrange, color: enconOrange }}
            data-testid="base-suppliers-btn"
          >
            <Star className="w-3.5 h-3.5 fill-orange-400" />
            Base Suppliers
            {baseSupplierCodes.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-white text-[10px] font-bold"
                style={{ backgroundColor: enconOrange }}>
                {baseSupplierCodes.length}
              </span>
            )}
          </button>
          {/* Toggle chevron */}
          <button
            onClick={() => setSuggestionsOpen(o => !o)}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            data-testid="suggestions-toggle"
          >
            {suggestionsOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
        </div>

        {suggestionsOpen && (
          <div className="px-5 pb-5 border-t">
            {loadingSuggestions ? (
              <div className="py-6 text-center text-gray-400 text-sm">Loading suggestions…</div>
            ) : suggestions.suppliers?.length === 0 ? (
              <div className="py-6 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
                <CheckCircle className="w-8 h-8 text-green-400" />
                {dailyInfo.has_daily
                  ? 'No products currently below the 50-day threshold.'
                  : 'Upload a daily stock file to generate order suggestions.'}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mt-3 mb-3">
                  <p className="text-xs text-gray-400">
                    {baseSupplierCodes.length > 0
                      ? <><Star className="w-3 h-3 inline fill-orange-400 text-orange-400 mr-1" />Base suppliers shown first — ordered by urgency</>
                      : 'Click a card to filter the table. Star = base supplier.'}
                  </p>
                  <span className="text-xs text-gray-400">
                    {suggestions.total_suggestions} lines · {suggestions.supplier_count} suppliers
                  </span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {suggestions.suppliers.map(sup => (
                    <SuggestionCard
                      key={sup.supplier_code}
                      supplier={sup}
                      active={activeSuggestionSupplier === sup.supplier_code}
                      isBase={baseSet.has(sup.supplier_code)}
                      onClick={() => handleSuggestionClick(sup)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Filters + Sort Bar ── */}
      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-3">
        {/* Row 1: Search + Supplier + Filter dropdown + Sort dropdown */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search product code, description, supplier…"
              className="pl-9"
              data-testid="sos-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Supplier dropdown */}
          <div className="relative min-w-[200px]">
            <select
              value={selectedSupplier}
              onChange={e => handleSupplierChange(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="sos-supplier-select"
            >
              <option value="">All Suppliers</option>
              {suppliers.map(s => (
                <option key={s.supplier_code} value={s.supplier_code}>
                  {s.supplier_name} ({s.count})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filter dropdown */}
          <div className="relative" ref={filterDropdownRef}>
            <button
              onClick={() => setFilterDropdownOpen(o => !o)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                activeFilters.length > 0 ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
              style={activeFilters.length > 0 ? { color: enconOrange } : {}}
              data-testid="filter-dropdown-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="font-medium">Filter{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${filterDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {filterDropdownOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFilterDropdownOpen(false)} />
                <div className="absolute left-0 mt-1 w-48 rounded-xl shadow-xl border bg-white z-50 overflow-hidden">
                  {[
                    { id: 'relevant', label: 'Relevant' },
                    { id: 'under-50', label: 'Under 50 days' },
                    { id: 'over-50', label: 'Over 50 days' },
                    { id: 'daily-comparison', label: 'Daily Comparison' },
                  ].map(opt => (
                    <label
                      key={opt.id}
                      className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors"
                      data-testid={`filter-opt-${opt.id}`}
                    >
                      <Checkbox
                        checked={activeFilters.includes(opt.id)}
                        onCheckedChange={() => toggleFilter(opt.id)}
                      />
                      <span className={`text-sm ${activeFilters.includes(opt.id) ? 'font-semibold' : 'text-gray-700'}`}
                        style={activeFilters.includes(opt.id) ? { color: enconOrange } : {}}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
              <X className="w-3.5 h-3.5 mr-1" /> Clear all
            </Button>
          )}
        </div>

        {/* Prefix pills (when supplier selected) */}
        {selectedSupplier && availablePrefixes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Prefixes:</span>
            {availablePrefixes.map(prefix => (
              <FilterPill key={prefix} active={selectedPrefixes.includes(prefix)} onClick={() => togglePrefix(prefix)}
                testId={`prefix-pill-${prefix}`}>
                {prefix}
              </FilterPill>
            ))}
            {selectedPrefixes.length > 0 && (
              <button onClick={() => setSelectedPrefixes([])} className="text-xs text-gray-400 hover:text-gray-600 px-2">
                Clear prefixes
              </button>
            )}
          </div>
        )}

        {/* Result count */}
        <div className="text-xs text-gray-400">
          {loadingItems ? 'Loading…' : `${displayItems.length.toLocaleString()} product${displayItems.length !== 1 ? 's' : ''}${items.length !== displayItems.length ? ` (filtered from ${items.length.toLocaleString()})` : ''}`}
          {dailyInfo.has_prev_daily && <span className="ml-2 text-blue-400">· Day comparison active</span>}
        </div>
      </div>

      {/* ── Main Table ── */}
      <div className="relative">
        {/* Full-width card — same original width, table never shrinks */}
        <div className="bg-white border-2 border-black rounded-xl shadow-sm overflow-hidden">
          {loadingItems ? (
            <div className="py-16 text-center text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-orange-300" />
              Loading products…
            </div>
          ) : displayItems.length === 0 ? (
            <div className="py-16 text-center text-gray-400 space-y-2">
              <Package className="w-10 h-10 mx-auto text-gray-300" />
              <p className="font-medium">{hasFilters ? 'No products match your filters' : 'No products found'}</p>
            </div>
          ) : (
            <table className="w-full" style={{ width: '100%', tableLayout: 'auto' }}>
              <colgroup>
                <col style={{ width: '130px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '72px' }} />
                <col style={{ width: '72px' }} />
                <col style={{ width: '88px' }} />
                <col style={{ width: '84px' }} />
                <col style={{ width: '84px' }} />
                <col style={{ width: '36px' }} />
                <col style={{ width: '44px' }} />
                <col style={{ width: '28px' }} />
              </colgroup>
              <thead>
                <tr className="border-b bg-gray-50" style={{ height: '44px' }}>
                  <SortableHeader label="Product Code" column="product_code" sortConfig={sortConfig} onSort={handleSort} align="left" />
                  <SortableHeader label="Description" column="description" sortConfig={sortConfig} onSort={handleSort} align="left" />
                  <SortableHeader label="Supplier" column="supplier_name" sortConfig={sortConfig} onSort={handleSort} align="left" />
                  <SortableHeader label="Stock" column="live_stock_qty" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Cover" column="live_cover_days" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Target (50d)" column="suggested_order_qty" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader label="To Order" column="_to_order" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHeader label="Value" column="live_stock_value" sortConfig={sortConfig} onSort={handleSort} />
                  <th className="px-1 py-3 text-center text-xs text-gray-400 uppercase tracking-wide" title="Notes">
                    <FileText className="w-3.5 h-3.5 mx-auto" />
                  </th>
                  <th className="py-3 text-center text-xs text-gray-500 uppercase tracking-wide" title="Ordered">
                    <CheckCircle className="w-3.5 h-3.5 mx-auto text-gray-400" />
                  </th>
                  <th
                    className="bg-gray-100 border-l border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setExtraColumnsOpen(o => !o)}
                    title={extraColumnsOpen ? 'Collapse extra info' : 'Show On Order / Allocated / 3M Qty'}
                    data-testid="extra-cols-toggle"
                  >
                    <div className="flex items-center justify-center">
                      <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ${extraColumnsOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {renderRows.map(row => {
                  if (row.type === 'banner') {
                    const { pinInfo, userReqCount, requestedAgo } = row;
                    return (
                      <tr key={row.key} className="border-b-0" style={{ height: `${BANNER_ROW_H}px` }}>
                        <td
                          colSpan={100}
                          className="px-4"
                          style={{
                            background: 'linear-gradient(90deg, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0.03) 100%)',
                            borderLeft: '3px solid #8b5cf6',
                          }}
                        >
                          <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#6d28d9' }}>
                            <ShoppingCart className="w-3 h-3 flex-shrink-0" />
                            <span>{pinInfo.user_name}</span>
                            {userReqCount > 1 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-bold"
                                style={{ backgroundColor: '#7c3aed' }}
                              >
                                {userReqCount} requests
                              </span>
                            )}
                            <span className="font-normal" style={{ color: '#a78bfa' }}>·</span>
                            <span className="font-normal" style={{ color: '#7c3aed' }}>Latest {requestedAgo}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <ItemRow
                      key={row.key}
                      item={row.item}
                      hasPrevDay={dailyInfo.has_prev_daily}
                      onToggleOrdered={toggleOrdered}
                      onSaveNotes={saveNotes}
                      extraColumnsOpen={extraColumnsOpen}
                      onToggleExtra={() => setExtraColumnsOpen(o => !o)}
                      isPinned={row.isPinned}
                      onOpenNotes={(it) => setNotesModalItem(it)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Side panel — absolutely positioned outside the card's right edge, into the page gap */}
        {!loadingItems && displayItems.length > 0 && (
          <div
            className="absolute top-0 overflow-hidden"
            style={{
              left: '100%',
              width: extraColumnsOpen ? (dailyInfo.has_prev_daily ? '296px' : '224px') : '0px',
              height: '100%',
              opacity: extraColumnsOpen ? 1 : 0,
              transition: 'width 200ms ease-out, opacity 200ms ease-out',
              background: 'linear-gradient(to right, #f8f9ff, #f3f4f8)',
              border: '1px solid #e5e7eb',
              borderLeft: 'none',
              borderRadius: '0 12px 12px 0',
              boxShadow: '2px 1px 4px 0 rgba(0,0,0,0.06)',
              pointerEvents: extraColumnsOpen ? 'auto' : 'none',
            }}
          >
            <table className="table-fixed w-full text-sm">
              <colgroup>
                <col style={{ width: '72px' }} />
                <col style={{ width: '72px' }} />
                <col style={{ width: '80px' }} />
                {dailyInfo.has_prev_daily && <col style={{ width: '72px' }} />}
              </colgroup>
              <thead>
                <tr className="border-b bg-blue-50/60" style={{ height: '44px' }}>
                  <th className="px-2 py-3 text-right text-xs text-blue-600 uppercase tracking-wide font-semibold whitespace-nowrap">On Order</th>
                  <th className="px-2 py-3 text-right text-xs text-purple-600 uppercase tracking-wide font-semibold whitespace-nowrap">Allocated</th>
                  <th className="px-2 py-3 text-right text-xs text-gray-500 uppercase tracking-wide font-semibold whitespace-nowrap">3M Qty</th>
                  {dailyInfo.has_prev_daily && <th className="px-2 py-3 text-right text-xs text-gray-500 uppercase tracking-wide font-semibold whitespace-nowrap">Change</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {renderRows.map(row => {
                  if (row.type === 'banner') {
                    // Spacer — exactly matches the banner row height in the main table
                    return <tr key={row.key} style={{ height: `${BANNER_ROW_H}px` }} aria-hidden="true" />;
                  }
                  const { item } = row;
                  const chg = item.qty_change;
                  const chgColor = chg == null ? '' : chg < -20 ? 'text-red-600 font-semibold' : chg < 0 ? 'text-amber-600' : chg > 0 ? 'text-green-600' : 'text-gray-400';
                  return (
                    <tr key={row.key} style={{ height: '52px' }}>
                      <td className="px-2 py-3 text-right font-mono tabular-nums text-blue-600">{fmt(item.on_order_quantity ?? 0)}</td>
                      <td className="px-2 py-3 text-right font-mono tabular-nums text-purple-600">{fmt(item.allocated_stock ?? 0)}</td>
                      <td className="px-2 py-3 text-right font-mono tabular-nums text-gray-600">{fmt(item.live_last_3m)}</td>
                      {dailyInfo.has_prev_daily && (
                        <td className={`px-2 py-3 text-right font-mono tabular-nums ${chgColor}`}>
                          {chg != null ? `${chg >= 0 ? '+' : ''}${fmt(chg)}` : '—'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Base Suppliers Modal ── */}
      <BaseSuppliersModal
        isOpen={baseSuppliersModalOpen}
        onClose={() => setBaseSuppliersModalOpen(false)}
        suppliers={suppliers}
        baseSupplierCodes={baseSupplierCodes}
        onSave={handleSaveBaseSuppliers}
      />

      {/* ── Stock Request Detail Modal ── */}
      {showRequestDetailModal && selectedRequestUser && (
        <StockRequestDetailModal
          userEmail={selectedRequestUser}
          requests={stockRequests.filter(r => r.user_email === selectedRequestUser)}
          onClose={() => { setShowRequestDetailModal(false); setSelectedRequestUser(null); }}
          onUpdate={(updated) => {
            setStockRequests(prev => prev.map(r =>
              r.id === updated.id ? { ...r, lines: updated.lines, product_count: updated.lines.filter(l => !l.purchased).length } : r
            ));
          }}
          branchId={BRANCH_ID}
        />
      )}

      {/* ── Stock History Modal ── */}
      {showHistoryModal && (
        <StockHistoryModal
          branchId={BRANCH_ID}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {/* ── Notes Modal ── */}
      {notesModalItem && (
        <NotesModal
          item={notesModalItem}
          onClose={() => setNotesModalItem(null)}
          onSave={(it, notes) => { saveNotes(it, notes); setNotesModalItem(prev => prev && prev.product_code === it.product_code ? { ...prev, notes } : prev); }}
        />
      )}
    </div>
  );
}

// ─── Notes Modal ──────────────────────────────────────────────────────────────
function NotesModal({ item, onClose, onSave }) {
  const [notes, setNotes] = useState(item.notes || '');
  const timerRef = useRef(null);

  const handleChange = (val) => {
    setNotes(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSave(item, val), 1200);
  };

  const handleSaveClose = () => {
    clearTimeout(timerRef.current);
    onSave(item, notes);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl border-2 border-black w-96 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-semibold text-gray-800 text-sm">{item.product_code}</div>
            <div className="text-xs text-gray-400 truncate max-w-xs">{item.description}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <textarea
          value={notes}
          onChange={e => handleChange(e.target.value)}
          className="w-full border border-black rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': enconOrange + '80', minHeight: '100px' }}
          placeholder="Add notes…"
          autoFocus
        />
        <div className="flex justify-between items-center mt-3">
          <span className="text-xs text-gray-400">Auto-saves as you type</span>
          <Button onClick={handleSaveClose} size="sm" className="text-white text-xs"
            style={{ backgroundColor: enconOrange }}>
            Save &amp; Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Item Row ─────────────────────────────────────────────────────────────────
function ItemRow({ item, hasPrevDay, onToggleOrdered, onSaveNotes, extraColumnsOpen, onToggleExtra, isPinned, onOpenNotes }) {
  const toOrderQty = item._to_order != null ? item._to_order : null;
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(item.product_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const toOrderHighlight = toOrderQty != null && toOrderQty > 0
    ? 'text-orange-600 font-bold'
    : 'text-gray-400';

  const hasNote = !!(item.notes && item.notes.trim());

  const rowBg = item.is_ordered
    ? 'bg-green-50 hover:bg-green-100'
    : isPinned
    ? 'bg-purple-50/60 hover:bg-purple-50'
    : 'hover:bg-gray-50';

  return (
    <tr
      className={`transition-colors text-sm ${rowBg}`}
      style={{ height: '52px' }}
      data-testid={`item-row-${item.product_code}`}
    >
      {/* Product code */}
      <td className="px-2 py-3 text-sm text-gray-700 overflow-hidden">
        <div className="flex items-center gap-1 min-w-0">
          {isPinned && <ShoppingCart className="w-3 h-3 flex-shrink-0 text-purple-500" title="In stock request" />}
          <span className="inline-block px-1 py-0.5 rounded text-white text-xs font-bold flex-shrink-0"
            style={{ backgroundColor: enconOrange, opacity: 0.85 }}>
            {item.prefix}
          </span>
          <button
            type="button"
            onClick={handleCopyCode}
            title={copied ? 'Copied!' : 'Click to copy'}
            className={`font-mono truncate text-xs px-1 py-0.5 rounded transition-all ${
              copied
                ? 'bg-green-100 text-green-700 font-semibold'
                : 'hover:bg-gray-100 hover:text-blue-700 text-gray-700 cursor-pointer'
            }`}
            data-testid={`copy-code-${item.product_code}`}
          >
            {copied ? '✓ Copied' : item.product_code}
          </button>
          {item.is_ordered && <span className="text-[10px] text-green-600 font-semibold uppercase flex-shrink-0">ordered</span>}
        </div>
      </td>

      {/* Description */}
      <td className="px-2 py-3 text-gray-700 overflow-hidden">
        <span className="truncate block text-sm leading-snug" title={item.description || ''}>{item.description || '—'}</span>
      </td>

      {/* Supplier */}
      <td className="px-2 py-3 text-sm text-gray-500 overflow-hidden">
        <span className="truncate block text-xs" title={item.supplier_name || item.supplier_code || ''}>
          {item.supplier_name || item.supplier_code || '—'}
        </span>
      </td>

      {/* Stock qty */}
      <td className="px-2 py-3 text-right text-sm font-mono font-medium tabular-nums">
        {fmt(item.live_stock_qty)}
      </td>

      {/* Cover days */}
      <td className={`px-2 py-3 text-right text-sm font-mono font-semibold tabular-nums ${coverClass(item.live_cover_days)}`}>
        {item.live_cover_days != null ? `${fmt(item.live_cover_days)}d` : '—'}
      </td>

      {/* Target (50d) */}
      <td className="px-2 py-3 text-right text-sm font-mono tabular-nums text-gray-500">
        {item._target50d != null ? fmt(item._target50d) : '—'}
      </td>

      {/* To Order */}
      <td className={`px-2 py-3 text-right text-sm font-mono tabular-nums ${toOrderHighlight}`}>
        {toOrderQty != null ? fmt(toOrderQty) : '—'}
      </td>

      {/* Stock Value */}
      <td className="px-2 py-3 text-right text-sm font-mono tabular-nums text-gray-600">
        {fmtCcy(item.live_stock_value)}
      </td>

      {/* Note icon */}
      <td className="px-1 py-3 text-center relative">
        <button
          onClick={() => onOpenNotes(item)}
          className={`p-1 rounded-full transition-colors ${hasNote ? 'text-orange-500 hover:bg-orange-50' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}
          title={hasNote ? item.notes : 'Add note…'}
          data-testid={`notes-btn-${item.product_code}`}
        >
          <FileText className="w-3.5 h-3.5" />
        </button>
      </td>

      {/* Ordered checkbox */}
      <td className="py-3 text-center">
        <Checkbox checked={!!item.is_ordered} onCheckedChange={() => onToggleOrdered(item)}
          data-testid={`ordered-cb-${item.product_code}`}
          className={item.is_ordered ? 'border-green-500' : ''} />
      </td>

      {/* Grey expand toggle */}
      <td
        className="bg-gray-100 border-l border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggleExtra}
      />
    </tr>
  );
}
